import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import Swal from 'sweetalert2';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';

const Finance = () => {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === 'ar';
    const [transactions, setTransactions] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0
    });
    const [formData, setFormData] = useState({
        amount: '',
        description: '',
        type: '',
        category: '',
        date: '',
        receipt: null,
        photo: null
    });
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [filters, setFilters] = useState({
        type: '',
        category: '',
        minAmount: '',
        maxAmount: '',
        startDate: '',
        endDate: ''
    });
    const [activeTab, setActiveTab] = useState('transactions');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [photoViewer, setPhotoViewer] = useState({
        isOpen: false,
        url: '',
        filename: '',
        isPdf: false
    });

    const categories = [
        'électricité', 'water', 'internet', 'cleaning_lady',
        'agent_payment', 'reservation', 'investment', 'autre', 'location'
    ];

    const fetchTransactions = useCallback(async () => {
        try {
            const response = await axios.get('https://krili-loto.com/api/transactions', {
                params: {
                    ...filters,
                    page: pagination.page,
                    limit: pagination.limit
                }
            });

            if (response.data.success) {
                setTransactions(response.data.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.data.total,
                    totalPages: response.data.totalPages
                }));
            }
        } catch (error) {
            console.error('Erreur de chargement des transactions', error);
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: t('error_loading_transactions'),
            });
        }
    }, [filters, pagination.page, pagination.limit]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    const formatDateForInput = (dateString) => {
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
    };

    const resetForm = () => {
        setFormData({
            amount: '',
            description: '',
            type: '',
            category: '',
            date: '',
            receipt: null,
            photo: null
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.amount || isNaN(parseFloat(formData.amount))) {
            return Swal.fire({
                icon: 'warning',
                title: t('required_field'),
                text: t('amount_required')
            });
        }
        if (!formData.type || !formData.date) {
            return Swal.fire({
                icon: 'warning',
                title: t('required_fields'),
                text: t('type_date_required')
            });
        }

        try {
            const data = new FormData();
            data.append("amount", formData.amount);
            data.append("description", formData.description || "");
            data.append("type", formData.type);
            data.append("category", formData.category || "");
            data.append("date", formData.date);

            if (formData.receipt) data.append("receipt", formData.receipt);
            if (formData.photo) data.append("photo", formData.photo);

            const url = isEditing
                ? `https://krili-loto.com/api/transactions/update/${editingId}`
                : 'https://krili-loto.com/api/transactions';

            await axios.post(url, data, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            await Swal.fire({
                icon: 'success',
                title: t('success'),
                text: isEditing ? t('transaction_updated_success') : t('transaction_added_success'),
            });

            resetForm();
            fetchTransactions();
            setIsEditing(false);
            setEditingId(null);
            setIsFormOpen(false);
        } catch (error) {
            console.error('Erreur:', error.response?.data || error.message);
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: `${isEditing ? t('update_error') : t('add_error')} ! ${error.response?.data?.message || error.message}`,
            });
        }
    };

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: files ? files[0] : value
        }));
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 when filters change
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleLimitChange = (e) => {
        setPagination(prev => ({
            ...prev,
            limit: parseInt(e.target.value),
            page: 1
        }));
    };

    const handleEdit = (id) => {
        const transactionToEdit = transactions.find(tx => tx.id === id);
        setFormData({
            amount: transactionToEdit.amount,
            description: transactionToEdit.description || '',
            type: transactionToEdit.type,
            category: transactionToEdit.category || '',
            date: formatDateForInput(transactionToEdit.date),
            receipt: null,
            photo: null
        });
        setIsEditing(true);
        setEditingId(id);
        setIsFormOpen(true);
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: t('confirm_delete_title'),
            text: t('confirm_delete_transaction'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('yes_delete'),
            cancelButtonText: t('cancel')
        });

        if (result.isConfirmed) {
            try {
                await axios.delete(`https://krili-loto.com/api/transactions/${id}`);
                await Swal.fire(t('deleted'), t('transaction_deleted_success'), 'success');
                fetchTransactions();
            } catch (error) {
                console.error('Erreur lors de la suppression', error);
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('delete_error'),
                });
            }
        }
    };

    const handleNewTransaction = () => {
        resetForm();
        setIsEditing(false);
        setEditingId(null);
        setIsFormOpen(true);
    };

    const openPhotoViewer = (receiptUrl, filename) => {
        const isPdf = receiptUrl?.toLowerCase().endsWith('.pdf');
        setPhotoViewer({
            isOpen: true,
            url: `https://krili-loto.com/api/uploads/${receiptUrl}`,
            filename: filename || receiptUrl || `receipt-${Date.now()}`,
            isPdf: isPdf
        });
    };

    const downloadPhoto = async () => {
        try {
            const response = await axios.get(photoViewer.url, {
                responseType: 'blob'
            });
            const blob = new Blob([response.data]);
            saveAs(blob, photoViewer.filename);

            Swal.fire({
                icon: 'success',
                title: t('download_success'),
                text: t('download_success_desc'),
            });
        } catch (error) {
            console.error('Download error:', error);
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: t('download_failed'),
            });
        }
    };

    const renderReceiptCell = (receipt) => {
        if (!receipt) return '—';

        return (
            <button
                onClick={() => openPhotoViewer(receipt)}
                className="text-indigo-600 hover:text-indigo-900 flex items-center"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${isRtl ? 'ml-1' : 'mr-1'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {t('view')}
            </button>
        );
    };

    const totalEncaissement = transactions
        .filter(t => t.type === 'encaissement')
        .reduce((sum, t) => sum + Number(t.amount), 0);

    const totalDecaissement = transactions
        .filter(t => t.type === 'décaissement')
        .reduce((sum, t) => sum + Number(t.amount), 0);

    const solde = totalEncaissement - totalDecaissement;

    const renderPagination = () => {
        const pages = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, pagination.page - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                    <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed ${isRtl ? 'ml-3' : ''}`}
                    >
                        {t('previous')}
                    </button>
                    <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed ${isRtl ? '' : 'ml-3'}`}
                    >
                        {t('next')}
                    </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div className="flex items-center space-x-4">
                        <p className="text-sm text-gray-700">
                            {t('showing')} <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> {t('to')}{' '}
                            <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> {t('on')}{' '}
                            <span className="font-medium">{pagination.total}</span> {t('results_count')}
                        </p>
                        <select
                            value={pagination.limit}
                            onChange={handleLimitChange}
                            className={`border border-gray-300 rounded-md text-sm py-1 px-2 ${isRtl ? 'mr-4' : 'ml-4'}`}
                        >
                            <option value={10}>10 {t('per_page')}</option>
                            <option value={25}>25 {t('per_page')}</option>
                            <option value={50}>50 {t('per_page')}</option>
                            <option value={100}>100 {t('per_page')}</option>
                        </select>
                    </div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                            onClick={() => handlePageChange(1)}
                            disabled={pagination.page === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="sr-only">Première page</span>
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                        <button
                            onClick={() => handlePageChange(pagination.page - 1)}
                            disabled={pagination.page === 1}
                            className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="sr-only">Précédent</span>
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </button>
                        {startPage > 1 && (
                            <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                ...
                            </span>
                        )}
                        {pages.map(page => (
                            <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === pagination.page
                                    ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                    }`}
                            >
                                {page}
                            </button>
                        ))}
                        {endPage < pagination.totalPages && (
                            <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                ...
                            </span>
                        )}
                        <button
                            onClick={() => handlePageChange(pagination.page + 1)}
                            disabled={pagination.page === pagination.totalPages}
                            className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="sr-only">Suivant</span>
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                        </button>
                        <button
                            onClick={() => handlePageChange(pagination.totalPages)}
                            disabled={pagination.page === pagination.totalPages}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="sr-only">Dernière page</span>
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10.293 15.707a1 1 0 010-1.414L14.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0zm-6 0a1 1 0 010-1.414L8.586 10 4.293 5.707a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </nav>
                </div>
            </div >
        );
    };

    return (
        <Sidebar>
            <div className={`min-h-screen bg-gray-50 ${isRtl ? 'rtl' : 'ltr'}`} dir={isRtl ? 'rtl' : 'ltr'}>
                <header className="bg-indigo-600 text-white p-4 shadow-md">
                    <div className="container mx-auto flex justify-between items-center">
                        <h1 className="text-2xl font-bold">{t('finance_management')}</h1>
                        <div className="flex space-x-4">
                            <button
                                onClick={handleNewTransaction}
                                className="bg-white text-indigo-600 px-4 py-2 rounded-lg font-medium hover:bg-indigo-50 transition"
                            >
                                {t('new_transaction')}
                            </button>
                        </div>
                    </div>
                </header>

                <main className="container mx-auto p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
                            <div className="flex items-center">
                                <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-gray-500 text-sm">Encaissements</p>
                                    <p className="text-2xl font-bold text-green-600">{totalEncaissement} DA</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-xl shadow-sm border border-red-100">
                            <div className="flex items-center">
                                <div className="p-3 rounded-full bg-red-100 text-red-600 mr-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-gray-500 text-sm">Décaissements</p>
                                    <p className="text-2xl font-bold text-red-600">{totalDecaissement} DA</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-xl shadow-sm border border-indigo-100">
                            <div className="flex items-center">
                                <div className="p-3 rounded-full bg-indigo-100 text-indigo-600 mr-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-gray-500 text-sm">Solde</p>
                                    <p className={`text-2xl font-bold ${solde >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {solde} DA
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mb-6 border-b border-gray-200">
                        <nav className="flex space-x-8 overflow-x-auto">
                            <button
                                onClick={() => setActiveTab('transactions')}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'transactions' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                            >
                                {t('transactions')}
                            </button>
                        </nav>
                    </div>

                    <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('type')}</label>
                                <select
                                    name="type"
                                    onChange={handleFilterChange}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                >
                                    <option value="">{t('all')}</option>
                                    <option value="encaissement">{t('encaissements')}</option>
                                    <option value="décaissement">{t('decaissements')}</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('category')}</label>
                                <select
                                    name="category"
                                    onChange={handleFilterChange}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                >
                                    <option value="">{t('all')}</option>
                                    {categories.map(cat => (
                                        <option key={cat} value={cat}>{t(cat)}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('amount')}</label>
                                <div className="flex space-x-2 rtl:space-x-reverse">
                                    <input
                                        type="number"
                                        name="minAmount"
                                        onChange={handleFilterChange}
                                        placeholder={t('min')}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                    />
                                    <input
                                        type="number"
                                        name="maxAmount"
                                        onChange={handleFilterChange}
                                        placeholder={t('max')}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('date')}</label>
                                <div className="flex space-x-2 rtl:space-x-reverse">
                                    <input
                                        type="date"
                                        name="startDate"
                                        onChange={handleFilterChange}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                    />
                                    <input
                                        type="date"
                                        name="endDate"
                                        onChange={handleFilterChange}
                                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Montant</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Catégorie</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Facture</th>
                                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {transactions.map(tx => (
                                        <tr key={tx.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {new Date(tx.date).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${tx.type === 'encaissement' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                    {tx.type}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {tx.amount} DA
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {tx.category || '—'}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                                                {tx.description || '—'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {renderReceiptCell(tx.receipt)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex space-x-2 justify-end">
                                                    <button
                                                        onClick={() => handleEdit(tx.id)}
                                                        className="text-indigo-600 hover:text-indigo-900"
                                                        title="Modifier"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(tx.id)}
                                                        className="text-red-600 hover:text-red-900"
                                                        title="Supprimer"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {transactions.length === 0 && (
                                        <tr>
                                            <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                                                Aucune transaction trouvée.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {pagination.totalPages > 1 && renderPagination()}
                    </div>
                </main>

                {isFormOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-screen overflow-y-auto">
                            <div className="p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h2 className="text-xl font-bold text-gray-900">
                                        {isEditing ? t('edit_transaction') : t('new_transaction')}
                                    </h2>
                                    <button
                                        onClick={() => {
                                            setIsFormOpen(false);
                                            resetForm();
                                            setIsEditing(false);
                                            setEditingId(null);
                                        }}
                                        className="text-gray-400 hover:text-gray-500"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('amount_da')}</label>
                                        <input
                                            type="number"
                                            name="amount"
                                            placeholder="0.00"
                                            value={formData.amount}
                                            onChange={handleChange}
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('description')}</label>
                                        <input
                                            type="text"
                                            name="description"
                                            placeholder={t('description')}
                                            value={formData.description}
                                            onChange={handleChange}
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                        />
                                    </div>
                                    <div className={`grid grid-cols-2 gap-4 ${isRtl ? 'rtl' : 'ltr'}`}>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('type')}</label>
                                            <select
                                                name="type"
                                                value={formData.type}
                                                onChange={handleChange}
                                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                                required
                                            >
                                                <option value="">{t('select')}</option>
                                                <option value="encaissement">{t('encaissements')}</option>
                                                <option value="décaissement">{t('decaissements')}</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('category')}</label>
                                            <select
                                                name="category"
                                                value={formData.category}
                                                onChange={handleChange}
                                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                            >
                                                <option value="">{t('select')}</option>
                                                {categories.map(cat => (
                                                    <option key={cat} value={cat}>{t(cat)}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('date')}</label>
                                        <input
                                            type="date"
                                            name="date"
                                            value={formData.date}
                                            onChange={handleChange}
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('justification')}</label>
                                        <div className="mt-1 flex items-center">
                                            <label className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                <span>{t('choose_file')}</span>
                                                <input
                                                    type="file"
                                                    name="receipt"
                                                    onChange={handleChange}
                                                    accept="image/*,application/pdf"
                                                    className="sr-only"
                                                />
                                            </label>
                                            <span className={`text-sm text-gray-500 ${isRtl ? 'mr-2' : 'ml-2'}`}>{formData.receipt?.name || t('no_file_selected')}</span>
                                        </div>
                                    </div>
                                    <div className={`pt-4 flex ${isRtl ? 'flex-row-reverse justify-start' : 'justify-end'} space-x-3 rtl:space-x-reverse`}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsFormOpen(false);
                                                resetForm();
                                                setIsEditing(false);
                                                setEditingId(null);
                                            }}
                                            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        >
                                            {t('cancel')}
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        >
                                            {isEditing ? t('save_changes') : t('save')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {photoViewer.isOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-auto">
                            <div className="p-4">
                                <div className="flex justify-between items-center mb-4">
                                    <h2 className="text-xl font-bold">Visualisation du justificatif</h2>
                                    <button
                                        onClick={() => setPhotoViewer({ isOpen: false, url: '', filename: '' })}
                                        className="text-gray-500 hover:text-gray-700"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="mb-4 flex justify-center bg-gray-100 rounded-lg overflow-hidden min-h-[400px]">
                                    {photoViewer.isPdf ? (
                                        <div className="w-full flex flex-col items-center justify-center p-8 text-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-24 w-24 text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                            </svg>
                                            <p className="text-xl font-medium text-gray-800 mb-2">Document PDF</p>
                                            <p className="text-gray-500 mb-6">{photoViewer.filename}</p>
                                            <a 
                                                href={photoViewer.url} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition shadow-lg inline-flex items-center"
                                            >
                                                Ouvrir dans un nouvel onglet
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                </svg>
                                            </a>
                                        </div>
                                    ) : (
                                        <img
                                            src={photoViewer.url}
                                            alt="Justificatif"
                                            className="max-w-full max-h-[70vh] object-contain"
                                        />
                                    )}
                                </div>

                                <div className="flex justify-end space-x-3">
                                    <button
                                        onClick={downloadPhoto}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 inline ${isRtl ? 'ml-2' : 'mr-2'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                        </svg>
                                        {t('download')}
                                    </button>
                                    <button
                                        onClick={() => setPhotoViewer({ isOpen: false, url: '', filename: '' })}
                                        className="px-4 py-2 border border-gray-300 rounded-md"
                                    >
                                        {t('close')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Sidebar>
    );
};

export default Finance;